// src/types/shop.ts
//
// The shop's catalogue as the page and the admin panel see it. Mirrors functions/src/shopCatalog.ts
// (55.45): categories and products are data the owner edits in Admin → Pood; a product's TYPE is
// what it does, which is code, so the list of types and their limits lives here and on the server.

import { dayKey } from '../utils/trainingCalendar';

export const TIME_BOOSTER_MAX_PERCENT = 50;

export type ItemType = 'timeBooster' | 'clickBooster' | 'tool' | 'gymItem' | 'trophy';
/** Rooms an industrial tool may need at home (55.55). Mirrors TOOL_ROOMS in functions/src/shopCatalog.ts. */
export type ToolRoom = 'tookoda' | 'garaaz';
export type Target = 'any' | 'work' | 'course';
export type Currency = 'pollid' | 'money';

export interface ItemTypeInfo {
    id: ItemType;
    label: string;
    /** What the value means, for the panel's field. */
    unit: string;
    min: number;
    max: number;
    explain: string;
}

export const ITEM_TYPES: ItemTypeInfo[] = [
    {
        id: 'timeBooster',
        label: 'Ajakiirendi',
        unit: '%',
        min: 1,
        max: TIME_BOOSTER_MAX_PERCENT,
        explain: 'Lühendab käimasolevat vahetust või koolitust väärtuse % võrra selle kogupikkusest. Kuni 50 %.',
    },
    {
        id: 'clickBooster',
        label: 'Treeningklikid',
        unit: 'klikki',
        min: 1,
        max: 200,
        explain: 'Lisab väärtuse jagu treeningklikke, mis tunni vahetusel ei kao.',
    },
    {
        id: 'tool',
        label: 'Tööriist',
        unit: 'aste',
        min: 1,
        max: 3,
        explain: 'Teeb tootmistöö võimalikuks. Aste 1 käsitööriist (×1), 2 elektritööriist (×2), 3 tööstuslik (×4, vajab kodus ruumi). Ostetakse üks kord ega kulu.',
    },
    {
        id: 'gymItem',
        label: 'Jõusaali ese',
        unit: 'aste',
        min: 1,
        max: 3,
        explain: 'Kodu jõusaali varustus (55.73): annab boonuse nimetatud omaduste treenimisele. Ühest reast on jõusaalis üks ese; parema astme ostmiseks müüakse vana 40 % hinnaga.',
    },
    {
        id: 'trophy',
        label: 'Luksusese',
        unit: 'tk',
        min: 1,
        max: 1,
        explain: 'Ei anna midagi peale uhkuse (55.74). Ostetakse üks kord ja on näha kodu juures ning mängija profiilil.',
    },
];

/** What gym equipment may train, and its labels (55.73). */
export const GYM_ATTRIBUTE_LABELS: Record<string, string> = {
    strength: 'Jõud', agility: 'Kiirus', dexterity: 'Osavus', intelligence: 'Intelligentsus', endurance: 'Vastupidavus',
};

export const TOOL_TIERS: Record<number, string> = { 1: 'Käsitööriist', 2: 'Elektritööriist', 3: 'Tööstuslik' };

export const TOOL_ROOMS: { id: ToolRoom; label: string }[] = [
    { id: 'tookoda', label: 'Töökoda' },
    { id: 'garaaz', label: 'Garaaž' },
];

export const TARGETS: { id: Target; label: string }[] = [
    { id: 'any', label: 'Töö ja koolitus' },
    { id: 'work', label: 'Ainult töö' },
    { id: 'course', label: 'Ainult koolitus' },
];

export const CURRENCIES: { id: Currency; label: string }[] = [
    { id: 'pollid', label: 'Pollid' },
    { id: 'money', label: 'Raha (€)' },
];

export interface ShopCategory {
    id: string;
    name: string;
    description: string;
    order: number;
    active: boolean;
}

export interface ShopItem {
    id: string;
    name: string;
    description: string;
    category: string;
    type: ItemType;
    value: number;
    target?: Target;
    /** Tools only: the production job the tool serves. */
    job?: string;
    /** Tools only, industrial ones and devices: the room they need at home. */
    room?: ToolRoom;
    /** Tools only: a workshop device (55.70) — one tier, one place in the workshop per workshop level. */
    device?: boolean;
    /** Trophies only (55.74): the room that must stand for it. */
    homeRoom?: string;
    /** Gym equipment only (55.73): its line, the gym level it is sold from, what it adds per attribute. */
    line?: string;
    gymLevel?: number;
    bonuses?: Record<string, number>;
    /** Devices only (55.71): the trade level it is sold from; absent, the setting `deviceSkillLevel`. */
    skillLevel?: number;
    currency: Currency;
    price: number;
    active: boolean;
    order: number;
}

export const typeInfo = (type: string): ItemTypeInfo | undefined => ITEM_TYPES.find((t) => t.id === type);

/** One line saying what a product does. */
export function effectLabel(item: { type: string; value: number; target?: Target; device?: boolean }): string {
    if (item.type === 'timeBooster') {
        const percent = Math.min(item.value, TIME_BOOSTER_MAX_PERCENT);
        const on = item.target === 'work' ? 'töövahetust' : item.target === 'course' ? 'koolitust' : 'vahetust või koolitust';
        return `−${percent} % ${on}`;
    }
    if (item.type === 'clickBooster') return `+${item.value} treeningklikki`;
    if (item.type === 'tool') return item.device ? 'Töökoja seade' : TOOL_TIERS[item.value] ?? 'Tööriist';
    if (item.type === 'gymItem') return 'Jõusaali ese';
    if (item.type === 'trophy') return 'Luksusese';
    return '';
}

export function priceLabel(price: number, currency: Currency): string {
    return currency === 'pollid' ? `${price} polli` : `${price.toLocaleString('et-EE')} €`;
}

export interface PurchaseResult {
    success: boolean;
    message: string;
    newBalance?: number;
    newPollidBalance?: number;
}

// ---- Daily booster limits ------------------------------------------------------------------

export type BoosterKind = 'work' | 'course' | 'clicks';

export const BOOSTER_DEFAULTS: Record<BoosterKind, number> = { work: 2, course: 2, clicks: 2 };
export const BOOSTER_SETTING: Record<BoosterKind, string> = {
    work: 'boosterDailyWork',
    course: 'boosterDailyCourse',
    clicks: 'boosterDailyClicks',
};

export interface BoosterDay { day: string; work: number; course: number; clicks: number }

/** Today's booster use, zero on a new Estonian day — the server counts the same way. */
export function boosterDayOf(stored: Partial<BoosterDay> | null | undefined, now: Date = new Date()): BoosterDay {
    const today = dayKey(now);
    if (!stored || stored.day !== today) return { day: today, work: 0, course: 0, clicks: 0 };
    const n = (v: unknown) => Math.max(0, Math.floor(Number(v ?? 0)) || 0);
    return { day: today, work: n(stored.work), course: n(stored.course), clicks: n(stored.clicks) };
}
